import React from 'react';
import { Shield, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '../ui/Input';

interface SecurityFormProps {
  form: {
    password: string;
    confirm_password: string;
    terms_accepted: boolean;
  };
  onChange: (form: any) => void;
  errors: { [key: string]: boolean };
}

export function SecurityForm({ form, onChange, errors }: SecurityFormProps) {
  const handleChange = (field: string, value: string | boolean) => {
    onChange({ ...form, [field]: value });
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-2 bg-teal-50 rounded-lg">
          <Shield className="h-6 w-6 text-teal-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">
          Sécurité
        </h2>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Mot de passe
        </label>
        <Input
          type="password"
          value={form.password}
          onChange={(e) => handleChange('password', e.target.value)}
          icon={<Lock className="h-5 w-5" />}
          error={errors.password}
          className="py-2.5"
          required
        />
        <div className="mt-3 space-y-2">
          <div className="text-xs text-gray-500">Votre mot de passe doit contenir :</div>
          <ul className="space-y-1">
            <PasswordRequirement 
              met={form.password.length >= 8}
              label="8 caractères minimum"
            />
            <PasswordRequirement 
              met={/[A-Z]/.test(form.password)}
              label="Une lettre majuscule"
            />
            <PasswordRequirement 
              met={/[a-z]/.test(form.password)}
              label="Une lettre minuscule"
            />
            <PasswordRequirement 
              met={/[0-9]/.test(form.password)}
              label="Un chiffre"
            />
          </ul>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Confirmer le mot de passe
        </label>
        <Input
          type="password"
          value={form.confirm_password}
          onChange={(e) => handleChange('confirm_password', e.target.value)}
          icon={<Lock className="h-5 w-5" />}
          error={errors.confirm_password}
          className="py-2.5"
          required
        />
      </div>

      <div className="flex items-start pt-4">
        <div className="flex items-center h-5">
          <input
            type="checkbox"
            checked={form.terms_accepted}
            onChange={(e) => handleChange('terms_accepted', e.target.checked)}
            className={`h-4 w-4 rounded border transition-colors duration-200 ease-in-out
              focus:ring-2 focus:ring-offset-2 focus:ring-teal-500
              ${errors.terms_accepted ? 'border-red-300 text-red-600' : 'border-gray-300 text-teal-600'}`}
          />
        </div>
        <div className="ml-3">
      <label className={`text-sm font-medium ${errors.terms_accepted ? 'text-red-700' : 'text-gray-700'}`}>
        J'accepte les conditions d'utilisation
      </label>
      <p className="mt-1 text-sm text-gray-500">
        En créant un compte, vous acceptez nos{' '}
        <Link to="/cgu" className="text-teal-600 hover:text-teal-500 underline">
          conditions d'utilisation
        </Link>{' '}
        et notre{' '}
        <Link to="/privacy" className="text-teal-600 hover:text-teal-500 underline">
          politique de confidentialité
        </Link>.
      </p>
    </div>
      </div>
    </div>
  );
}

function PasswordRequirement({ met, label }: { met: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${met ? 'bg-green-100' : 'bg-gray-100'}`}>
        <div className={`w-2 h-2 rounded-full ${met ? 'bg-green-500' : 'bg-gray-300'}`}></div>
      </div>
      <span className={met ? 'text-green-700' : 'text-gray-500'}>
        {label}
      </span>
    </li>
  );
}